// 2ECOMP: follow popup — popup isolado com mini-mapa MapLibre para seguir device
import {
  useRef, useEffect, useState, useMemo, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Rnd } from 'react-rnd';
import maplibregl from 'maplibre-gl';
import {
  alpha, IconButton, Tooltip, Typography, Box,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  DragIndicator as DragIcon,
  Navigation as NavigationIcon,
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  CropSquare as RestoreIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { useAttributePreference, usePreference } from '../../common/util/preferences';
import usePersistedState from '../../common/util/usePersistedState';
import useMapStyles from '../../map/core/useMapStyles';
import { devicesActions } from '../../store';
import { formatSpeed, getStatusColor } from '../../common/util/formatter';
import { mapIconKey } from '../../map/core/preloadImages';
import { icons } from '../../map/core/icons3d';
import { loadImage, prepareIcon } from '../../map/core/mapUtil';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// 2ECOMP: follow popup — dimensões padrão
const DEFAULT_WIDTH = 480;
const DEFAULT_HEIGHT = 380;
const TRAIL_MAX_POINTS = 50; // 2ECOMP: follow popup — máximo de pontos no trail

// 2ECOMP: Seguir Dispositivo — persistir estado (tamanho/posição por device)
const POPUP_STORAGE_KEY = (id) => `followPopup_${id}`;

const getSavedPopupState = (deviceId) => {
  try {
    const saved = localStorage.getItem(POPUP_STORAGE_KEY(deviceId));
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const savePopupState = (deviceId, size, pos) => {
  try {
    localStorage.setItem(POPUP_STORAGE_KEY(deviceId), JSON.stringify({ size, position: pos }));
  } catch { /* ignore */ }
};

const useStyles = makeStyles()((theme) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      borderRadius: 12,
      overflow: 'hidden',
      background: isDark ? 'rgba(20, 22, 28, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(16px)',
      border: `1px solid ${alpha(isDark ? '#ffffff' : '#000000', 0.1)}`,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      height: 36,
      minHeight: 36,
      padding: '0 4px 0 8px',
      gap: 6,
      cursor: 'grab',
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      userSelect: 'none',
      '&:active': {
        cursor: 'grabbing',
      },
    },
    dragIcon: {
      fontSize: 16,
      opacity: 0.4,
      flexShrink: 0,
    },
    navIcon: {
      fontSize: 16,
      color: '#00d4aa',
      flexShrink: 0,
      animation: 'followPulse 2s ease-in-out infinite',
    },
    deviceName: {
      flex: 1,
      fontSize: 13,
      fontWeight: 600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: theme.palette.text.primary,
    },
    statusBadge: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.5,
      padding: '1px 6px',
      borderRadius: 4,
      flexShrink: 0,
      textTransform: 'uppercase',
    },
    statusMoving: {
      background: alpha('#00d4aa', 0.15),
      color: '#00d4aa',
    },
    statusStopped: {
      background: alpha(theme.palette.warning.main, 0.15),
      color: theme.palette.warning.main,
    },
    statusOffline: {
      background: alpha(theme.palette.error.main, 0.15),
      color: theme.palette.error.main,
    },
    closeButton: {
      padding: 4,
      flexShrink: 0,
      '& svg': { fontSize: 18 },
    },
    mapContainer: {
      flex: 1,
      position: 'relative',
      minHeight: 0,
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
      height: 32,
      minHeight: 32,
      padding: '0 10px',
      gap: 12,
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      fontSize: 11,
      color: theme.palette.text.secondary,
      overflow: 'hidden',
    },
    footerItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    footerAddress: {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    // 2ECOMP: follow popup — header action buttons (minimize, layers)
    headerAction: {
      padding: 3,
      flexShrink: 0,
      '& svg': { fontSize: 16 },
    },
    // 2ECOMP: follow popup — style picker dropdown
    stylePicker: {
      position: 'absolute',
      top: 36,
      right: 4,
      zIndex: 10,
      maxHeight: 240,
      overflowY: 'auto',
      borderRadius: 8,
      background: isDark ? 'rgba(30, 32, 38, 0.97)' : 'rgba(255, 255, 255, 0.97)',
      backdropFilter: 'blur(12px)',
      border: `1px solid ${alpha(isDark ? '#ffffff' : '#000000', 0.12)}`,
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
      padding: '4px 0',
    },
    styleItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '6px 12px',
      fontSize: 11,
      cursor: 'pointer',
      color: theme.palette.text.secondary,
      transition: 'background 0.1s',
      '&:hover': {
        background: alpha(theme.palette.primary.main, 0.08),
      },
    },
    styleItemActive: {
      color: '#00d4aa',
      fontWeight: 600,
    },
    // Keyframes para pulse do ícone Navigation
    '@global': {
      '@keyframes followPulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.4 },
      },
    },
  };
});

// 2ECOMP: mobile-fix v2 — aceita mobileFullscreen para renderizar sem Rnd
const DeviceFollowPopup = ({ deviceId, index, mobileFullscreen = false }) => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const t = useTranslation();

  // 2ECOMP: follow popup — estados de minimize e style picker
  const [minimized, setMinimized] = useState(false);
  const [stylePickerOpen, setStylePickerOpen] = useState(false);
  const sizeBeforeMinRef = useRef(null);
  const rndRef = useRef(null);
  const stylePickerRef = useRef(null);

  // 2ECOMP: follow popup — dados do Redux
  const device = useSelector((state) => state.devices.items[deviceId]);
  const position = useSelector((state) => state.session.positions[deviceId]);
  const speedUnit = useAttributePreference('speedUnit', 'kn');

  // 2ECOMP: follow popup — resolver style do mapa (independente por popup)
  const mapStyles = useMapStyles();
  const mainMapStyle = usePreference('map', 'locationIqStreets');
  const [selectedMapStyle] = usePersistedState('selectedMapStyle', mainMapStyle);
  const [popupMapStyle, setPopupMapStyle] = usePersistedState(`followPopupStyle-${deviceId}`, selectedMapStyle);

  const availableStyles = useMemo(() => mapStyles.filter((s) => s.available), [mapStyles]);

  const styleUrl = useMemo(() => {
    const found = mapStyles.find((s) => s.id === popupMapStyle && s.available);
    if (found) return found.style;
    // fallback para o estilo do mapa principal
    const mainFound = mapStyles.find((s) => s.id === selectedMapStyle && s.available);
    if (mainFound) return mainFound.style;
    return 'https://tiles.openfreemap.org/styles/liberty';
  }, [mapStyles, popupMapStyle, selectedMapStyle]);

  // 2ECOMP: follow popup — refs para mini-mapa, trail e estado
  const mapContainerRef = useRef(null);
  const miniMapRef = useRef(null);
  const trailCoordsRef = useRef([]); // 2ECOMP: follow popup — array de [lng, lat]
  const mapReadyRef = useRef(false); // 2ECOMP: follow popup — flag de mapa carregado
  const imageCacheRef = useRef({}); // 2ECOMP: follow popup — cache de ícones 3D para mini-mapa
  const abortRef = useRef(null); // 2ECOMP: follow popup — AbortController para fetch de histórico

  // 2ECOMP: Seguir Dispositivo — persistir estado: restaurar tamanho/posição salvos
  const savedState = useMemo(() => getSavedPopupState(deviceId), [deviceId]);

  const defaultSize = useMemo(() => (
    savedState?.size || { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT }
  ), [savedState]);

  const defaultPosition = useMemo(() => (
    savedState?.position || {
      x: Math.max(0, (window.innerWidth - DEFAULT_WIDTH) / 2) + (index * 24),
      y: Math.max(0, (window.innerHeight - DEFAULT_HEIGHT) / 2) + (index * 24),
    }
  ), [savedState, index]);

  // 2ECOMP: follow popup — renderizar trail como LineString GeoJSON
  const updateTrail = useCallback(() => {
    const miniMap = miniMapRef.current;
    if (!miniMap || trailCoordsRef.current.length < 2) return;

    const geojson = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: trailCoordsRef.current,
      },
    };

    if (miniMap.getSource('follow-trail')) {
      miniMap.getSource('follow-trail').setData(geojson);
      return;
    }

    miniMap.addSource('follow-trail', {
      type: 'geojson',
      data: geojson,
    });

    miniMap.addLayer({
      id: 'follow-trail-line',
      type: 'line',
      source: 'follow-trail',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#00d4aa',
        'line-width': 3,
        'line-opacity': 0.7,
      },
    });
  }, []);

  // 2ECOMP: follow popup — performance fix — carregar ícone 3D no mini-mapa
  // Replica padrão do mapa principal (icons3d/index.js):
  // 1. Placeholder 1x1 SÍNCRONO (evita "Image X could not be loaded")
  // 2. Geração async do SVG real
  // 3. removeImage + addImage para substituir placeholder
  const loadMiniMapIcon = useCallback(async (miniMap, iconId) => {
    if (!miniMap.getStyle()) return;
    if (imageCacheRef.current[iconId]) {
      // Cache hit — substituir ou adicionar direto
      try {
        if (miniMap.hasImage(iconId)) miniMap.removeImage(iconId);
        miniMap.addImage(iconId, imageCacheRef.current[iconId], { pixelRatio: window.devicePixelRatio });
      } catch { /* ignore */ }
      return;
    }
    if (miniMap.hasImage(iconId)) return; // placeholder já adicionado, geração em andamento

    const [reqCategory, reqColor, reqRotation] = iconId.split('-');
    const colorMap = { info: '#2196F3', success: '#4caf50', error: '#f44336', neutral: '#FE795D' };
    const color = colorMap[reqColor] || '#FE795D';

    // 2ECOMP: follow popup — performance fix — placeholder SÍNCRONO imediato
    try {
      miniMap.addImage(iconId, { width: 1, height: 1, data: new Uint8Array([0, 0, 0, 0]) });
    } catch { /* ignore */ }

    // Resolver categoria — fallback para 'default' se desconhecida (ex: brownfield)
    const iconFn = icons[reqCategory] || icons.default;

    // Geração async do ícone real
    const svg = iconFn(reqRotation, color);
    const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    try {
      const img = await loadImage(url);
      const imageData = prepareIcon(img);
      imageCacheRef.current[iconId] = imageData;
      // Substituir placeholder pelo ícone real
      if (miniMap.getStyle()) {
        try {
          if (miniMap.hasImage(iconId)) miniMap.removeImage(iconId);
          miniMap.addImage(iconId, imageData, { pixelRatio: window.devicePixelRatio });
        } catch { /* ignore */ }
      }
    } finally {
      URL.revokeObjectURL(url);
    }
  }, []);

  // 2ECOMP: follow popup — inicializar mini-mapa MapLibre
  useEffect(() => {
    if (!mapContainerRef.current) return undefined;

    const lng = position?.longitude || 0;
    const lat = position?.latitude || 0;

    const miniMap = new maplibregl.Map({
      container: mapContainerRef.current,
      style: styleUrl,
      zoom: 15,
      center: [lng, lat],
      attributionControl: false,
    });

    miniMapRef.current = miniMap;

    // 2ECOMP: follow popup — performance fix — handler styleimagemissing
    // Placeholder síncrono para qualquer imagem faltante (evita "Image X could not be loaded")
    miniMap.on('styleimagemissing', (e) => {
      if (/^[a-z]+[0-9]?-[a-z]+-[0-9.]+$/.test(e.id)) {
        loadMiniMapIcon(miniMap, e.id);
      } else if (!miniMap.hasImage(e.id)) {
        // Fallback: pixel transparente para IDs desconhecidos
        try {
          miniMap.addImage(e.id, { width: 1, height: 1, data: new Uint8Array([0, 0, 0, 0]) });
        } catch { /* ignore */ }
      }
    });

    // 2ECOMP: follow popup — performance fix — silenciar erros conhecidos do MapLibre
    miniMap.on('error', (ev) => {
      const msg = ev?.error?.message || '';
      // Erros de expressão nula (course null) e imagem não encontrada são esperados
      if (msg.includes('Expected value to be of type number') || msg.includes('could not be loaded')) return;
    });

    miniMap.on('load', async () => {
      mapReadyRef.current = true; // 2ECOMP: follow popup — mapa pronto

      // 2ECOMP: follow popup — marker como Symbol Layer (idêntico ao mapa principal)
      // 2ECOMP: follow popup — performance fix — null safety para course null
      const course = position?.course ?? 0;
      const iconDegrees = Math.round(course / 22.5) * 22.5 % 360;
      const category = mapIconKey(device?.category);
      const colorKey = getStatusColor(device?.status, position?.attributes?.motion) || 'neutral';
      const iconId = `${category}-${colorKey}-${iconDegrees}`;

      // Pré-carregar ícone antes de adicionar a layer
      await loadMiniMapIcon(miniMap, iconId);

      miniMap.addSource('device-point', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: position ? [position.longitude, position.latitude] : [lng, lat],
          },
          properties: {
            category: category ?? 'default',
            color: colorKey ?? 'neutral',
            iconDegrees: iconDegrees ?? 0,
            rotation: course % 22.5,
          },
        },
      });

      miniMap.addLayer({
        id: 'device-point-layer',
        type: 'symbol',
        source: 'device-point',
        layout: {
          'icon-image': iconId,
          'icon-size': 0.75,
          'icon-rotate': ['coalesce', ['get', 'rotation'], 0],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
        },
      });

      // 2ECOMP: follow popup — buscar histórico recente para trail inicial
      try {
        const controller = new AbortController();
        abortRef.current = controller;
        const now = new Date();
        const from = new Date(now.getTime() - 30 * 60 * 1000); // últimos 30 min
        const response = await fetch(
          `/api/positions?deviceId=${deviceId}`
          + `&from=${from.toISOString()}&to=${now.toISOString()}`,
          { credentials: 'include', signal: controller.signal },
        );
        if (response.ok) {
          const history = await response.json();
          if (history.length > 0) {
            trailCoordsRef.current = history
              .slice(-TRAIL_MAX_POINTS)
              .map((p) => [p.longitude, p.latitude]);
            updateTrail();
          }
        }
      } catch (e) {
        if (e.name === 'AbortError') return; // 2ECOMP: follow popup — silenciar abort
        console.debug('[FollowPopup] Trail history not loaded:', e.message);
      }
    });

    return () => {
      // 2ECOMP: follow popup — cleanup CRÍTICO para evitar memory leak
      if (abortRef.current) abortRef.current.abort();
      mapReadyRef.current = false;
      trailCoordsRef.current = [];
      imageCacheRef.current = {};
      miniMap.remove();
      miniMapRef.current = null;
    };
  }, []); // inicializa apenas uma vez

  // 2ECOMP: follow popup — atualizar marker (Symbol Layer), câmera e trail a cada position update
  useEffect(() => {
    if (!mapReadyRef.current || !miniMapRef.current || !position) return;

    const miniMap = miniMapRef.current;
    const { longitude, latitude } = position;
    // 2ECOMP: follow popup — performance fix — null safety para course null
    const course = position.course ?? 0;
    const iconDegrees = Math.round(course / 22.5) * 22.5 % 360;
    const category = mapIconKey(device?.category);
    const colorKey = getStatusColor(device?.status, position?.attributes?.motion) || 'neutral';
    const iconId = `${category}-${colorKey}-${iconDegrees}`;

    // Atualizar posição e propriedades do marker (Symbol Layer)
    const source = miniMap.getSource('device-point');
    if (source) {
      source.setData({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [longitude, latitude],
        },
        properties: {
          category: category ?? 'default',
          color: colorKey ?? 'neutral',
          iconDegrees: iconDegrees ?? 0,
          rotation: course % 22.5,
        },
      });

      // Atualizar icon-image se status/direção mudou
      try {
        miniMap.setLayoutProperty('device-point-layer', 'icon-image', iconId);
      } catch {
        // Layer pode não estar pronta ainda
      }
    }

    // Seguir o device com animação suave
    miniMap.easeTo({
      center: [longitude, latitude],
      duration: 300,
      essential: true,
    });

    // Acumular trail
    const newPoint = [longitude, latitude];
    trailCoordsRef.current = [
      ...trailCoordsRef.current.slice(-(TRAIL_MAX_POINTS - 1)),
      newPoint,
    ];
    updateTrail();
  }, [position, device, updateTrail]);

  // 2ECOMP: follow popup — resize o mapa quando o container mudar de tamanho
  const handleResize = useCallback((e, dir, ref) => {
    if (!minimized && ref) {
      sizeBeforeMinRef.current = { width: ref.offsetWidth, height: ref.offsetHeight };
    }
    requestAnimationFrame(() => {
      miniMapRef.current?.resize();
    });
  }, [minimized]);

  // 2ECOMP: follow popup — minimize/restore handlers
  const handleMinimize = useCallback(() => {
    const rnd = rndRef.current;
    if (!rnd) return;
    if (!minimized) {
      // Salvar tamanho atual antes de minimizar
      const el = rnd.resizableElement?.current;
      if (el) {
        sizeBeforeMinRef.current = { width: el.offsetWidth, height: el.offsetHeight };
      }
      const curSize = sizeBeforeMinRef.current || defaultSize;
      rnd.updateSize({ width: curSize.width, height: 36 });
      setMinimized(true);
    } else {
      rnd.updateSize(sizeBeforeMinRef.current || defaultSize);
      setMinimized(false);
      // resize do mapa com delay para CSS transition completar
      setTimeout(() => {
        miniMapRef.current?.resize();
      }, 160);
    }
  }, [minimized]);

  // 2ECOMP: follow popup — re-adicionar sources/layers após troca de estilo
  const reAddMapLayers = useCallback((miniMap) => {
    // 2ECOMP: follow popup — performance fix — null safety para course null
    const course = position?.course ?? 0;
    const iconDegrees = Math.round(course / 22.5) * 22.5 % 360;
    const category = mapIconKey(device?.category);
    const colorKey = getStatusColor(device?.status, position?.attributes?.motion) || 'neutral';
    const iconId = `${category}-${colorKey}-${iconDegrees}`;

    // Re-add device marker
    if (!miniMap.getSource('device-point')) {
      miniMap.addSource('device-point', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: position ? [position.longitude, position.latitude] : [0, 0],
          },
          properties: {
            category: category ?? 'default',
            color: colorKey ?? 'neutral',
            iconDegrees: iconDegrees ?? 0,
            rotation: course % 22.5,
          },
        },
      });
    }
    if (!miniMap.getLayer('device-point-layer')) {
      miniMap.addLayer({
        id: 'device-point-layer',
        type: 'symbol',
        source: 'device-point',
        layout: {
          'icon-image': iconId,
          'icon-size': 0.75,
          'icon-rotate': ['coalesce', ['get', 'rotation'], 0],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
        },
      });
    }

    // Re-add trail
    if (trailCoordsRef.current.length >= 2 && !miniMap.getSource('follow-trail')) {
      miniMap.addSource('follow-trail', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: trailCoordsRef.current },
        },
      });
      miniMap.addLayer({
        id: 'follow-trail-line',
        type: 'line',
        source: 'follow-trail',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#00d4aa', 'line-width': 3, 'line-opacity': 0.7 },
      });
    }
  }, [position, device]);

  // 2ECOMP: follow popup — performance fix — trocar estilo do mapa
  // Diferir setStyle para fora do click handler via requestAnimationFrame
  // evita bloqueio de 855-1537ms no main thread
  const handleStyleChange = useCallback((styleId) => {
    setPopupMapStyle(styleId);
    setStylePickerOpen(false);

    requestAnimationFrame(() => {
      const miniMap = miniMapRef.current;
      if (!miniMap || !mapReadyRef.current) return;

      const found = mapStyles.find((s) => s.id === styleId && s.available);
      if (!found) return;

      // Bloquear updates de position durante transição
      mapReadyRef.current = false;
      // Limpar cache de ícones pois setStyle destroi tudo
      imageCacheRef.current = {};

      miniMap.once('style.load', () => {
        mapReadyRef.current = true;
        reAddMapLayers(miniMap);
      });
      miniMap.setStyle(found.style);
    });
  }, [mapStyles, setPopupMapStyle, reAddMapLayers]);

  // 2ECOMP: follow popup — fechar style picker ao clicar fora
  useEffect(() => {
    if (!stylePickerOpen) return undefined;
    const handleClickOutside = (e) => {
      if (stylePickerRef.current && !stylePickerRef.current.contains(e.target)) {
        setStylePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [stylePickerOpen]);

  // 2ECOMP: follow popup — status do device
  const getStatusInfo = () => {
    if (!device) return { label: '?', className: classes.statusOffline };
    if (device.status === 'offline') {
      return { label: 'OFFLINE', className: classes.statusOffline };
    }
    if (position?.attributes?.motion) {
      return { label: t('positionMotion') || 'MOVENDO', className: classes.statusMoving };
    }
    return { label: t('deviceStatusOnline') || 'PARADO', className: classes.statusStopped };
  };

  // 2ECOMP: follow popup — tempo relativo do último update
  const timeAgo = useMemo(() => {
    if (!position?.fixTime) return '—';
    return dayjs(position.fixTime).fromNow(true);
  }, [position?.fixTime]);

  // Guarda de segurança: device removido durante follow
  if (!device) return null;

  const statusInfo = getStatusInfo();

  // 2ECOMP: mobile-fix v2 — conteúdo compartilhado entre desktop (Rnd) e mobile (fullscreen)
  const content = (
    <div className={classes.container} style={mobileFullscreen ? { borderRadius: 0, height: '100%' } : undefined}>
      {/* Header */}
      <div className={cx(classes.header, !mobileFullscreen && 'follow-popup-drag-handle')} style={mobileFullscreen ? { cursor: 'default' } : undefined}>
        {!mobileFullscreen && <DragIcon className={classes.dragIcon} />}
        <NavigationIcon className={classes.navIcon} />
        <Typography className={classes.deviceName} component="span">
          {device.name}
        </Typography>
        <Box
          component="span"
          className={cx(classes.statusBadge, statusInfo.className)}
        >
          {statusInfo.label}
        </Box>
        {/* 2ECOMP: follow popup — botão de troca de estilo */}
        {!minimized && (
          <Tooltip title={t('mapTitle') || 'Mapa'}>
            <IconButton
              className={classes.headerAction}
              onClick={() => setStylePickerOpen((prev) => !prev)}
              size="small"
            >
              <LayersIcon />
            </IconButton>
          </Tooltip>
        )}
        {/* 2ECOMP: follow popup — botão minimize/restore (desktop only) */}
        {!mobileFullscreen && (
          <Tooltip title={minimized ? (t('windowRestore') || 'Restaurar') : (t('windowMinimize') || 'Minimizar')}>
            <IconButton
              className={classes.headerAction}
              onClick={handleMinimize}
              size="small"
            >
              {minimized ? <RestoreIcon /> : <MinimizeIcon />}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('deviceStopFollow') || 'Parar de seguir'}>
          <IconButton
            className={classes.closeButton}
            onClick={() => dispatch(devicesActions.removeFollowPopup(deviceId))}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>

      {/* Mini-mapa MapLibre — escondido quando minimizado */}
      <div
        ref={mapContainerRef}
        className={classes.mapContainer}
        style={minimized ? { display: 'none' } : undefined}
      />

      {/* 2ECOMP: follow popup — style picker dropdown */}
      {stylePickerOpen && !minimized && (
        <div ref={stylePickerRef} className={classes.stylePicker}>
          {availableStyles.map((s) => (
            <div
              key={s.id}
              className={cx(classes.styleItem, s.id === popupMapStyle && classes.styleItemActive)}
              onClick={() => handleStyleChange(s.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleStyleChange(s.id)}
            >
              {s.title}
            </div>
          ))}
        </div>
      )}

      {/* Footer — velocidade, endereço, timestamp */}
      {!minimized && (
        <div className={classes.footer}>
          <span className={classes.footerItem}>
            {position ? formatSpeed(position.speed, speedUnit, t) : '—'}
          </span>
          <span className={cx(classes.footerItem, classes.footerAddress)}>
            {position?.address || '—'}
          </span>
          <span className={classes.footerItem}>
            {timeAgo}
          </span>
        </div>
      )}
    </div>
  );

  // 2ECOMP: mobile-fix v2 — mobile fullscreen (sem Rnd drag/resize)
  if (mobileFullscreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {content}
      </Box>
    );
  }

  // Desktop: Rnd drag/resize
  return (
    <Rnd
      ref={rndRef}
      default={{
        x: defaultPosition.x,
        y: defaultPosition.y,
        width: defaultSize.width,
        height: defaultSize.height,
      }}
      minWidth={300}
      minHeight={minimized ? 36 : 250}
      maxWidth={700}
      maxHeight={minimized ? 36 : 550}
      bounds="window"
      dragHandleClassName="follow-popup-drag-handle"
      enableResizing={!minimized}
      onResize={handleResize}
      // 2ECOMP: Seguir Dispositivo — persistir estado ao redimensionar
      onResizeStop={(e, dir, ref, delta, pos) => {
        handleResize(e, dir, ref);
        const newSize = { width: ref.offsetWidth, height: ref.offsetHeight };
        savePopupState(deviceId, newSize, pos);
      }}
      // 2ECOMP: Seguir Dispositivo — persistir estado ao mover
      onDragStop={(e, d) => {
        const rnd = rndRef.current;
        const el = rnd?.resizableElement?.current;
        const curSize = el
          ? { width: el.offsetWidth, height: el.offsetHeight }
          : defaultSize;
        savePopupState(deviceId, curSize, { x: d.x, y: d.y });
      }}
      style={{ zIndex: 1200 + index, position: 'fixed' }}
    >
      {content}
    </Rnd>
  );
};

export default DeviceFollowPopup;
